import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepSkeleton } from '@components/BookingFlow/Common/BookingStepSkeleton';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { compareLicensePlates, customerRoutes } from 'noddi-util';
import {
  useBookingAddress,
  useBookingCars,
  useBookingSelectedSalesItemsCars,
  useIsTireHotelPickUpStepValid,
  useLocalizationLanguage
} from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useMobileBookingNeedsCustomerVisit } from '../../../../hooks/useMobileBookingNeedsCustomerVisit';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { CarWithTirePickUpOptions } from './CarWithTirePickUpOptions';
import { MultipleCarsWithPickUpOptions } from './MultipleCarsWithPickUpOptions';

export const TireHotelPickup = () => {
  const { currentBrand } = useCurrentBrand();
  const language = useLocalizationLanguage();

  const selectedAddress = useBookingAddress();
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const bookingCars = useBookingCars();
  const navigate = useNavigate();

  const carsWithTirePickupOptions = selectedSalesItemsCars.filter((car) => car.isWheelPickupRequired);
  const hasMultipleCarsWithTirePickup = carsWithTirePickupOptions.length > 1;

  // Pair each pickup-requiring selection with its car in the draft, so the per-car view gets a car
  // it can count on.
  const carsInBooking = carsWithTirePickupOptions.flatMap((item) => {
    const car = bookingCars.find((bookingCar) => compareLicensePlates(bookingCar.licensePlate, item.licensePlate));
    return car ? [{ item, car }] : [];
  });

  // The single-car branch renders `carsInBooking`, so dropping every unpaired car empties it — and
  // an empty `carsWithTirePickupOptions` lands here too. The multi-car branch reads the unpaired
  // list, so it is unaffected.
  const hasNothingToPickUp = !hasMultipleCarsWithTirePickup && carsInBooking.length === 0;

  const isTireHotelPickUpStepValid = useIsTireHotelPickUpStepValid();
  const { needsCustomerVisit, isPending: isNeedsCustomerVisitPending } = useMobileBookingNeedsCustomerVisit({
    enabled: isTireHotelPickUpStepValid
  });

  const {
    data: pickUpOptions,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getTireHotelPickupSalesItemsOptions,
    input: {
      brandDomain: currentBrand,
      addressId: selectedAddress?.id as number,
      selectedSalesItemIds: [...new Set(carsWithTirePickupOptions.map((car) => car.salesItemId))],
      language
    },
    queryConfig: {
      enabled: !!selectedAddress
    }
  });

  // Both conditions come from the draft, so they must be checked before the query: the fetch is
  // disabled without an address, which React Query reports as `isPending` forever — gating these
  // on it left the step spinning on a deep link instead of ever reaching this page.
  if (!selectedAddress) {
    return <BookingStepUnavailablePage missing='address' />;
  }

  if (hasNothingToPickUp) {
    return <BookingStepUnavailablePage missing='services' />;
  }

  // Chrome (title / back / bottom bar) stays mounted while the options load.
  return (
    <BookingScreen
      progress={getMobileBookingProgress('services')}
      onNextClicked={() => {
        // Without a customer visit there is no time to pick: the booking takes its wheel pickup's day.
        navigate(
          needsCustomerVisit === false
            ? customerRoutes.newBookingSummary.getPath()
            : customerRoutes.newBookingTimeWindow.getPath()
        );
      }}
      disableNextButton={!isTireHotelPickUpStepValid || isNeedsCustomerVisitPending}
      validationVariant='tireHotelPickupRequired'
      title={t`Where should we pick up your tires?`}
    >
      {error ? (
        <ApiErrorMessageWithTrans error={error} />
      ) : isPending ? (
        <BookingStepSkeleton rows={2} rowClassName='h-48' />
      ) : hasMultipleCarsWithTirePickup ? (
        <MultipleCarsWithPickUpOptions
          carsWithTirePickupOptions={carsWithTirePickupOptions}
          pickUpOptions={pickUpOptions}
        />
      ) : (
        <div className='flex flex-col gap-6'>
          {carsInBooking.map(({ item, car }) => (
            <CarWithTirePickUpOptions
              key={item.licensePlate.number}
              licensePlate={item.licensePlate}
              pickUpOptions={pickUpOptions}
              car={car}
            />
          ))}
        </div>
      )}
    </BookingScreen>
  );
};
